/**
 * UcFormB — форма «Бонус / Штраф» UC-панели (screen-22, эталон renderFormB
 * js:69-98): оператор (Dropdown с балансом в meta + filter), seg Бонус/Штраф
 * (штраф — красный .pen), сумма NumPadPop money+suffix UC+decimals 2 (УД4,
 * max 10000 — эталон js:82), причина обязательна (≤200 — серверный лимит
 * sanitize). «Провести» disabled до валидности; знак amount выбирает тип на
 * сервере (bonus|penalty). Ошибка POST — inline ferr (не alert); успех —
 * хук перезагрузил данные и вспыхнул первой строкой леджера, форма закрыта.
 */
import { useState } from "react";
import { Dropdown, NumPadPop } from "../../components/shared";
import type { UcBalanceRow, UcBonusBody } from "../../api/ucTypes";
import { CheckIco, FCell, SelBtn, opItems } from "./UcFormParts";
import { fmtUC, NB } from "./ucFmt";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import ucss from "./uc.module.css";

export function UcFormB({ balances, busy, submit, onClose }: {
  balances: UcBalanceRow[];
  busy: boolean;
  submit: (b: UcBonusBody) => Promise<string | null>;
  onClose: () => void;
}) {
  const [op, setOp] = useState<string | null>(null);
  const [type, setType] = useState<"bonus" | "penalty">("bonus");
  const [sum, setSum] = useState(0);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [np, setNp] = useState<HTMLElement | null>(null);

  const opName = op ? balances.find((b) => b.operator_id === op)?.operator_name ?? null : null;
  const valid = !!op && sum > 0 && !!reason.trim();

  const doSubmit = () => {
    if (!op || !valid || busy) return;
    setErr(null);
    void submit({
      operator_id: op,
      amount: type === "penalty" ? -sum : sum,
      description: reason.trim(),
    }).then((e) => (e ? setErr(e) : onClose()));
  };

  return (
    <div className={qcss.cform}>
      <div className={qcss.s17head} style={{ margin: 0, height: 20 }}>
        <h3 className={`${cfg.secT} ${qcss.cformT}`} style={{ fontSize: 10 }}>Бонус / Штраф</h3>
      </div>
      <div className={ucss.frow2}>
        <FCell label="Оператор">
          <SelBtn value={opName} ph="выбрать…" onOpen={(e) => setDd(e.currentTarget)} />
        </FCell>
        <FCell label="Тип">
          <div className={`${ucss.seg} ${ucss.gr}`} style={{ width: "fit-content" }}>
            {([["bonus", "Бонус"], ["penalty", "Штраф"]] as const).map(([t, l]) => (
              <button
                key={t}
                type="button"
                className={type === t ? `${ucss.on} ${t === "penalty" ? ucss.pen : ""}` : ""}
                onClick={() => setType(t)}
              >
                {l}
              </button>
            ))}
          </div>
        </FCell>
      </div>
      <div className={ucss.frow2}>
        <FCell label="Сумма">
          <button
            type="button"
            className={`${cfg.teNumbtn} ${ucss.tnUc}`}
            onClick={(e) => setNp(e.currentTarget)}
          >
            <span className={`${ucss.tv} ${qcss.num}`}>
              {sum > 0
                ? `${type === "penalty" ? "−" : "+"}${fmtUC(sum)}${NB}UC`
                : <span className={ucss.ph}>0 UC</span>}
            </span>
          </button>
        </FCell>
        <FCell label="Причина">
          <input
            className={qcss.txt}
            placeholder="Причина — обязательна"
            value={reason}
            maxLength={200}
            onChange={(e) => { setReason(e.target.value); setErr(null); }}
          />
        </FCell>
      </div>
      {err ? <div className={ucss.ferr}>{err}</div> : null}
      <div className={qcss.apair} style={{ marginTop: 3 }}>
        <button type="button" className={`${qcss.abtn} ${qcss.cf}`} disabled={!valid || busy} onClick={doSubmit}>
          <CheckIco />Провести
        </button>
        <button type="button" className={`${qcss.abtn} ${qcss.cl}`} onClick={onClose}>Отмена</button>
      </div>
      <Dropdown
        open={!!dd}
        anchor={dd}
        multi={false}
        filter
        selectedIds={op ? [op] : []}
        items={opItems(balances)}
        onClose={() => setDd(null)}
        onCommit={(ids) => { const id = ids[0]; if (id != null) { setOp(String(id)); setErr(null); } }}
      />
      <NumPadPop
        open={!!np}
        anchor={np}
        label="Сумма"
        mode="money"
        suffix="UC"
        decimals={2}
        value={sum || 0}
        max={10000}
        onCommit={(v) => { setSum(v || 0); setErr(null); }}
        onClose={() => setNp(null)}
      />
    </div>
  );
}
